import { useState, useEffect } from 'react';
import { propertiesApi } from '@/lib/api';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';

export const PropertyModal = ({ open, onClose, property, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    address: '',
    notes: '',
    status: 'active',
    monthly_rent: '',
    monthly_expenses: '',
  });

  useEffect(() => {
    if (property) {
      setFormData({
        address: property.address || '',
        notes: property.notes || '',
        status: property.status || 'active',
        monthly_rent: property.monthly_rent?.toString() || '',
        monthly_expenses: property.monthly_expenses?.toString() || '',
      });
    } else {
      setFormData({
        address: '',
        notes: '',
        status: 'active',
        monthly_rent: '',
        monthly_expenses: '',
      });
    }
  }, [property, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.address.trim()) {
      toast.error('Address is required');
      return;
    }

    setLoading(true);
    try {
      const data = {
        address: formData.address.trim(),
        notes: formData.notes.trim(),
        status: formData.status,
        monthly_rent: parseFloat(formData.monthly_rent) || 0,
        monthly_expenses: parseFloat(formData.monthly_expenses) || 0,
      };

      if (property) {
        await propertiesApi.update(property.id, data);
        toast.success('Property updated successfully');
      } else {
        await propertiesApi.create(data);
        toast.success('Property created successfully');
      }
      onSuccess();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save property';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">
            {property ? 'Edit Property' : 'Add New Property'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="address">Property Address *</Label>
            <Input
              id="address"
              data-testid="property-address-input"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="123 Main Street, London, SW1A 1AA"
              className="mt-1.5"
              disabled={loading}
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              data-testid="property-notes-input"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Any additional notes about this property..."
              className="mt-1.5 resize-none"
              rows={3}
              disabled={loading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="monthly_rent">Monthly Rent (£)</Label>
              <Input
                id="monthly_rent"
                type="number"
                step="0.01"
                min="0"
                data-testid="property-rent-input"
                value={formData.monthly_rent}
                onChange={(e) => setFormData({ ...formData, monthly_rent: e.target.value })}
                placeholder="0.00"
                className="mt-1.5"
                disabled={loading}
              />
            </div>
            <div>
              <Label htmlFor="monthly_expenses">Monthly Expenses (£)</Label>
              <Input
                id="monthly_expenses"
                type="number"
                step="0.01"
                min="0"
                data-testid="property-expenses-input"
                value={formData.monthly_expenses}
                onChange={(e) => setFormData({ ...formData, monthly_expenses: e.target.value })}
                placeholder="0.00"
                className="mt-1.5"
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) => setFormData({ ...formData, status: value })}
              disabled={loading}
            >
              <SelectTrigger className="mt-1.5" data-testid="property-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              data-testid="property-submit-btn"
              className="bg-slate-900 hover:bg-slate-800"
              disabled={loading}
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="spinner"></span>
                  {property ? 'Saving...' : 'Creating...'}
                </span>
              ) : (
                property ? 'Save Changes' : 'Create Property'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyModal;
